import { Button } from '@/components/shadcn/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/shadcn/dropdown-menu'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useRestClient } from '@/contexts/QueryProvider'
import { IconDeleteOutline } from '@icons/IconDeleteOutline'
import { IconEditOutline } from '@icons/IconEditOutline'
import { IconMenuOutline } from '@icons/IconMenuOutline'
import { IconAddUserOutline } from '@icons/IconAddUserOutline'
import { IconV2EyeOutline } from '@icons/IconV2EyeOutline'
import { useState } from 'react'
import { toast } from 'sonner'
import { SkillForm } from '../Forms/SkillForm'
import { SkillMembersDialog } from '../Forms/SkillMembersDialog'
import { SkillSummary } from './platformAgentSkillsTableUtils'

export function SkillActionsMenu({ skill }: { skill: SkillSummary }) {
  const [membersOpen, setMembersOpen] = useState(false)
  const { getSkillApiGetSkills, deleteSkillApiDeleteSkill, postSkillApiSetSkillPublic } = useRestClient()

  const invalidateSkills = () => {
    getSkillApiGetSkills.invalidateQueries(undefined)
  }

  const { mutate: deleteSkill } = deleteSkillApiDeleteSkill.useMutation(undefined, {
    onSuccess: () => {
      invalidateSkills()
      toast.success('Skill successfully deleted')
    },
    onError: () => {
      toast.error('Error deleting skill')
    },
  })

  const { mutate: setSkillPublic, isPending: isUpdatingVisibility } = postSkillApiSetSkillPublic.useMutation(undefined, {
    onSuccess: () => {
      invalidateSkills()
      toast.success('Skill visibility updated')
    },
    onError: () => {
      toast.error('Error updating skill visibility')
    },
  })

  const handleDeleteSkill = () => {
    if (!skill.id) return
    deleteSkill({ path: { skillId: skill.id } })
  }

  const handleToggleVisibility = () => {
    if (!skill.id) return
    setSkillPublic({
      path: { skillId: skill.id },
      body: { isPublic: !skill.isPublic },
    })
  }

  if (!skill.canEdit) {
    return null
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="size-8 justify-self-end">
            <IconMenuOutline size={16} />
            <span className="sr-only">Open menu</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          onCloseAutoFocus={(e) => {
            e.preventDefault()
          }}
        >
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <SkillForm
            selectedSkill={skill}
            sheetTitle="Edit skill"
            sheetDescription="Update skill metadata and visibility."
            onSubmit={invalidateSkills}
            sheetTrigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <div className="flex items-center gap-2">
                  <IconEditOutline size={16} />
                  <span>Edit skill</span>
                </div>
              </DropdownMenuItem>
            }
          />
          <DropdownMenuItem disabled={isUpdatingVisibility} onSelect={handleToggleVisibility}>
            <div className="flex items-center gap-2">
              <IconV2EyeOutline size={16} />
              <span>{skill.isPublic ? 'Make private' : 'Make public'}</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem
            onSelect={(e) => {
              e.preventDefault()
              setMembersOpen(true)
            }}
          >
            <div className="flex items-center gap-2">
              <IconAddUserOutline size={16} />
              <span>Manage members</span>
            </div>
          </DropdownMenuItem>
          <ConfirmDialog
            title="Confirm delete"
            description={`Are you sure you want to delete "${skill.name}"?`}
            onConfirm={handleDeleteSkill}
            confirmVariant="destructive"
            trigger={
              <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                <div className="flex items-center gap-2">
                  <IconDeleteOutline size={16} />
                  <span>Delete skill</span>
                </div>
              </DropdownMenuItem>
            }
          />
        </DropdownMenuContent>
      </DropdownMenu>
      <SkillMembersDialog skill={skill} open={membersOpen} onOpenChange={setMembersOpen} />
    </>
  )
}
